import React, { FunctionComponent } from "react"
import {
    Dialog,
    DialogTitle,
    Typography,
    TextField,
    Grid,
    DialogContent,
    DialogActions,
    Button
} from "@material-ui/core"
import { Prompt } from "../../../../typings/types"
import { FieldContainer } from "../../../components/FormHelpers/FieldContainer"
import { useFormik } from "formik"
import { LoadingButton } from "../../../components/LoadingButton"
import { useApolloClient, useMutation } from "@apollo/client"
import { UPDATE_MEDIA } from "../../../mutations/UPDATE_MEDIA"
import { useNotification } from "../../../common/hooks/useNotification"
import { editablePromptFieldsSchema, EditablePromptFields } from "./validationSchema"
import styles from "./EditFileModal.module.scss"
import SEARCH_PROMPTS from "../../../queries/SEARCH_PROMPTS"
import { getOperationNames } from "../../../common/GqlUtilities"
import GET_PROMPT_CARD_DETAILS from "../../../queries/GET_PROMPT_CARD_DETAILS"

interface EditFileModalProps {
    prompt: Prompt
    open: boolean
    onClose: () => void
}

const EditFileDescriptionText = () => (
    <Typography data-testid="edit-file-description" variant="caption">
        Update field and click 'Save' to apply changes.
    </Typography>
)

export const EditFileModal: FunctionComponent<EditFileModalProps> = ({ prompt, open, onClose }) => {
    const { notifySuccess } = useNotification()
    const client = useApolloClient()

    const [updateMedia, { loading: updateMediaLoading, error: updateMediaError }] = useMutation(
        UPDATE_MEDIA,
        {
            onCompleted: () => {
                notifySuccess(`Successfully updated name: ${prompt.name} to ${values.name} `)
                closeForm()
            },
            onError: () => {},
            refetchQueries: getOperationNames([SEARCH_PROMPTS, GET_PROMPT_CARD_DETAILS])
        }
    )

    const { values, errors, handleChange, handleBlur, handleSubmit, handleReset } = useFormik<
        EditablePromptFields
    >({
        initialValues: {
            name: prompt.name
        },
        validationSchema: editablePromptFieldsSchema({
            initialValues: { name: prompt.name },
            client: client
        }),
        validateOnMount: false,
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: (values: EditablePromptFields) =>
            updateMedia({
                variables: {
                    updateMediaInput: {
                        audioLocation: prompt.audioLocation,
                        newName: values.name
                    }
                }
            })
    })

    const closeForm = () => {
        onClose()
        handleReset({})
    }

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
            <DialogTitle data-testid="edit-file-title">Edit File</DialogTitle>
            <DialogContent dividers>
                <Grid container direction="column">
                    <Grid item>
                        <EditFileDescriptionText />
                    </Grid>
                    <Grid item>
                        <form id="edit-form" data-testid="edit-file-form" onSubmit={handleSubmit}>
                            <FieldContainer label="Name" error={errors.name}>
                                <TextField
                                    id="edit-name-input-field"
                                    name="name"
                                    value={values.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!errors.name}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    inputProps={{
                                        "data-testid": "edit-name-input-field"
                                    }}
                                />
                            </FieldContainer>
                        </form>
                    </Grid>
                </Grid>
            </DialogContent>
            <Grid container direction="row" alignItems="center" justify="space-between">
                <Grid item className={styles.EditFileModal__updateError}>
                    {updateMediaError && (
                        <Typography data-testid="edit-file-error" variant="caption" color="error">
                            {updateMediaError.message}
                        </Typography>
                    )}
                </Grid>
                <Grid item className={styles.EditFileModal__actionButtons}>
                    <DialogActions>
                        <Button
                            data-testid="edit-file-action-cancel"
                            variant="contained"
                            onClick={closeForm}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            buttonId="edit-file-action-submit"
                            text="Save"
                            loading={updateMediaLoading}
                            disabled={updateMediaLoading || !!errors.name}
                            submit
                            form="edit-form"
                        />
                    </DialogActions>
                </Grid>
            </Grid>
        </Dialog>
    )
}
